// useInitiatedByMe / useAwaitingMe / useDecideWorkflow — sidebar 「鈴鐺」用。
//
// Phase 1B: 30 秒 polling refetch (handoff §4.5/§4.7 confirmed)。 Phase 2 之後
// 再考慮 SSE / WebSocket。 staleTime 也設 30 秒, 讓 polling tick 之間 cache
// 不會被頁面切換打掉。

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useActor } from './actor-store.ts';
import { api } from './api.ts';
import { RBAC_ROLE_LABEL } from './rbac-labels.ts';

export type WorkflowType =
  | 'access_request'
  | 'fund_status_change'
  | 'project_status_change'
  | 'party_status_change'
  | 'internal_signup_review'
  | 'money_movement_review';
export type WorkflowScope = 'system' | 'fund' | 'project' | 'investor' | 'cross';
export type WorkflowEntityType = 'fund' | 'project' | 'investor' | 'commitment' | 'party';
export type WorkflowStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'completed';
export type WorkflowAssignedRole =
  | 'administrator'
  | 'fund_manager'
  | 'sponsor_partner'
  | 'project_manager';

export interface WorkflowRow {
  id: string;
  ref_no: string | null;
  management_company_id: string;
  type: string;
  scope: WorkflowScope;
  entity_type: WorkflowEntityType | null;
  entity_id: string | null;
  /** 後端讀取時解析的標的名稱 (2026-07-20; 舊單的 payload 快照沒有名字) */
  entity_label?: string | null;
  status: WorkflowStatus;
  current_step: string | null;
  initiated_by: string;
  initiated_by_name: string | null;
  initiated_by_code: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_code: string | null;
  assigned_role: WorkflowAssignedRole | null;
  title: string;
  description: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  /**
   * 申請日期 (台北)。 工作流程沒有草稿狀態 —— 建立即送出, 所以 created_at 就是
   * 申請日。 ⚠ 不要用 `created_at.slice(0,10)`, 那是 UTC 的日子 (同 approvals)。
   */
  applied_on: string;
  updated_at: string;
  decided_at: string | null;
  decided_by: string | null;
  decided_by_name: string | null;
  decision_note: string | null;
  /** V105: 重新提交鏈 — 若此 wf 是因為 #X 被退回而重送, parent_workflow_id=X */
  parent_workflow_id: string | null;
}

// 2026-08-01 效能：輪詢從 30s 放寬到 60s，改用「回到分頁就立刻更新」補回即時性。
// 待辦數是 4 支查詢各自輪詢（workflows/awaiting-me、workflows、parties?pending、
// approvals?inbox），30 秒一輪 = 開著頁面整天就是持續打 —— 而 Azure PG 只是
// Burstable B1ms。 refetchOnWindowFocus 讓「切回來看一眼」反而比原本更即時，
// 掛在背景時則不再空轉（全域預設是 refetchOnWindowFocus: false，這裡逐支開）。
const POLL_MS = 60_000;

export function useInitiatedByMe() {
  const actor = useActor((s) => s.actor);
  return useQuery<WorkflowRow[]>({
    queryKey: ['workflows', 'initiated-by-me', actor],
    queryFn: () => api<WorkflowRow[]>('/api/v1/workflows/initiated-by-me', { actor }),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export function useAwaitingMe() {
  const actor = useActor((s) => s.actor);
  return useQuery<WorkflowRow[]>({
    queryKey: ['workflows', 'awaiting-me', actor],
    queryFn: () => api<WorkflowRow[]>('/api/v1/workflows/awaiting-me', { actor }),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

// 全頁 list — 跟 sidebar 兩個 endpoint 互補, 接 GET /api/v1/workflows 完整版
// (mine filter additive 可複合 status/scope/type)
export interface WorkflowListFilters {
  scope?: WorkflowScope;
  type?: string;
  status?: WorkflowStatus;
  mine?: 'initiated' | 'awaiting';
  limit?: number;
  offset?: number;
}

export function useWorkflowsList(filters: WorkflowListFilters, opts?: { enabled?: boolean }) {
  const actor = useActor((s) => s.actor);
  const qs = new URLSearchParams();
  if (filters.scope) qs.set('scope', filters.scope);
  if (filters.type) qs.set('type', filters.type);
  if (filters.status) qs.set('status', filters.status);
  if (filters.mine) qs.set('mine', filters.mine);
  qs.set('limit', String(filters.limit ?? 200));
  if (filters.offset) qs.set('offset', String(filters.offset));

  return useQuery<WorkflowRow[]>({
    queryKey: ['workflows', 'list', filters, actor],
    queryFn: () => api<WorkflowRow[]>(`/api/v1/workflows?${qs.toString()}`, { actor }),
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

export function useWorkflowDetail(id: number | string | null) {
  const actor = useActor((s) => s.actor);
  return useQuery<WorkflowRow>({
    queryKey: ['workflows', 'detail', id, actor],
    queryFn: () => api<WorkflowRow>(`/api/v1/workflows/${id}`, { actor }),
    enabled: id !== null && id !== undefined,
    staleTime: 30_000,
  });
}

export interface CreateWorkflowInput {
  type: WorkflowType;
  scope: WorkflowScope;
  entity_type?: WorkflowEntityType;
  entity_id?: number | string;
  assigned_to?: number | string;
  assigned_role?: WorkflowAssignedRole;
  title: string;
  description?: string;
  payload?: Record<string, unknown>;
  /** V105: 重新提交時帶 — 後端驗 parent 是 rejected + 同一發起人 */
  parent_workflow_id?: number | string;
}

// V105: 我發起且被退回的 — 給鈴鐺 + sidebar badge 用 (需要 user attention:
// 看退回理由 + 修內容重送 OR 不再處理)
/** 2026-08-03: 這些型別的「退回」是**終點**，不是「打回去改了再送」。
 *
 *  金流覆核 (money_movement_review) 是**事後追認** —— 錢早就動了，退回的意思是
 *  「我記錄了這筆有異常」，沒有東西可以修正後重送。 但引擎把 rejected 一律當成
 *  「發起人要處理」→ 覆核單的發起人又是系統掛在動作人名下 → 退回自己的覆核單之後
 *  待辦永遠 +1、清不掉（使用者實際踩到：「我的簽單不見了」，其實是掉進這一格）。
 *
 *  ⚠ 狀態變更那些**不能**列進來 —— 它們的退回真的需要發起人改了再送。 */
const TERMINAL_ON_REJECT = new Set(['money_movement_review']);

/** 我發起、被退回、**且還需要我處理**的 workflow（給待辦數字用）。 */
export function useRejectedInitiatedByMe() {
  const q = useWorkflowsList({ mine: 'initiated', status: 'rejected' });
  const actionable = useMemo(
    () => (q.data ?? []).filter((w) => !TERMINAL_ON_REJECT.has(w.type)),
    [q.data],
  );
  return { ...q, data: q.data ? actionable : undefined };
}

// V349 批 3b：這 5 個流程可以改走簽核引擎（多關卡 / 路線可設定 / 知會關）。
// 切換點做在這個 hook 裡面而不是各發起點 —— 4 個呼叫端（狀態編輯器、專案升級
// modal、申請加入按鈕、退回後重送）都不必改，而且政策一關就整組退回舊引擎。
const MIGRATED_TO_APPROVAL = new Set([
  'fund_status_change',
  'project_status_change',
  'party_status_change',
  'access_request',
]);

export function useCreateWorkflow() {
  const actor = useActor((s) => s.actor);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWorkflowInput) => {
      // gate 開著 → 走簽核引擎（草稿→送審一次完成）；關著 → 照舊走 workflow。
      // 查詢失敗不擋送出，退回舊引擎（保守：寧可用舊路也不要送不出去）。
      let gates: string[] = [];
      if (MIGRATED_TO_APPROVAL.has(input.type)) {
        try {
          gates = await api<string[]>('/api/v1/approval-gates', { actor });
        } catch {
          gates = [];
        }
      }
      if (gates.includes(input.type) && input.entity_type && input.entity_id) {
        // status='approved' = 送出當場就核准、狀態已經變了（呼叫端要刷新標的）
        return api<{ id: number; request_no: string; status?: string }>(
          '/api/v1/approvals/entity-gate',
          {
            actor,
            method: 'POST',
            json: {
              type: input.type,
              entity_type: input.entity_type,
              entity_id: input.entity_id,
              title: input.title,
              description: input.description,
              payload: input.payload,
            },
          },
        );
      }
      return api<{ id: number; status?: string }>('/api/v1/workflows', {
        actor,
        method: 'POST',
        json: input,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] });
      // 走簽核引擎時單子落在 approvals 那邊 — 兩邊都刷新，待辦數才正確
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['approval'] });
    },
  });
}

export interface EntityGatePreview {
  /** 'workflow' = 這個流程沒開事前簽核，送出走舊的工作流程（沒有路線可算） */
  engine: 'workflow' | 'approval';
  /** true = 送出當場就核准（例如送單人自己就是唯一簽核人）；null = 算不出來 */
  immediate?: boolean | null;
  /** 送出後要等的審核關，依順序；names 是這一關可以簽的人 */
  steps?: Array<{ seq: number; label: string; names: string[] }>;
}

/** 2026-10-01：送審前先算出實際路線 —— 後端跑同一支 approval_submit 再整筆撤掉，
 *  所以講出來的跟真的送出去一致（含「自己送就跳過自己那關」）。 */
export function useEntityGatePreview(input: {
  type: WorkflowType;
  entity_type: 'fund' | 'project' | 'party';
  entity_id: number | string;
  payload?: Record<string, unknown>;
  enabled?: boolean;
}) {
  const actor = useActor((s) => s.actor);
  const { enabled = true, ...body } = input;
  return useQuery<EntityGatePreview>({
    queryKey: ['entity-gate-preview', actor, body],
    queryFn: () =>
      api<EntityGatePreview>('/api/v1/approvals/entity-gate/preview', {
        actor,
        method: 'POST',
        json: { ...body, entity_id: Number(body.entity_id) },
      }),
    enabled,
    // 路線跟著人與設定走，開抽屜時算一次就好；不要因為切視窗重算
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

/** 把預覽結果講成一句話。回 null = 算不出來，呼叫端用原本的通用說明。 */
export function describeGatePreview(p: EntityGatePreview | undefined): string | null {
  if (!p || p.engine !== 'approval' || p.immediate == null) return null;
  if (p.immediate) return '送出後立即生效，不需要等其他人核准。';
  const steps = (p.steps ?? []).map((s) => {
    const who = s.names.length > 0 ? s.names.join(' 或 ') : s.label;
    return `${who}（${s.label}）`;
  });
  if (steps.length === 0) return null;
  return steps.length === 1
    ? `送出後由 ${steps[0]} 簽核，核准後才會生效。`
    : `送出後依序由 ${steps.join(' → ')} 簽核，全部核准後才會生效。`;
}

export type DecideAction = 'approve' | 'reject' | 'cancel';

export function useDecideWorkflow() {
  const actor = useActor((s) => s.actor);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number | string; action: DecideAction; decision_note?: string }) =>
      api<void>(`/api/v1/workflows/${args.id}/decide`, {
        actor,
        method: 'PATCH',
        json: { action: args.action, decision_note: args.decision_note },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] });
      // access_request 通過會 INSERT ura → 鎖 icon 要消失
      void qc.invalidateQueries({ queryKey: ['my-ura'] });
    },
  });
}

// ============================================================
// Workflow type catalog — 主流程設計清單。
//
// 跟 SESSION_HANDOFF_2026-05-10 §4.2 同步: 每加一個 workflow type 在這裡
// 註冊 → catalog 頁面自動列出 (規劃中) → backend handler 接好之後把 status
// 從 'planned' 翻成 'live' 即可。
//
// status 'live' 必須跟 use-workflows.ts WorkflowType Zod enum + backend
// handler 對齊 (Phase 1 只 access_request); 'planned' 純 UI 顯示, 不會被
// POST /workflows 接受。
// ============================================================
export interface WorkflowTypeMeta {
  key: string;
  scope: WorkflowScope;
  label_zh: string;
  description: string;
  status: 'live' | 'planned';
  applies_to?: ReadonlyArray<WorkflowEntityType>;
}

// ⚠ description **不寫「誰核准」**（2026-09-19）。 核准人是簽核路線的設定, 在「簽核路線」頁
// 可以改, 而且說明就顯示在關卡的正上方 —— 寫死在這裡只會過期: 原本 11 條寫「管理員核准」,
// 實際上帳號審核送人資主管、Call / 分配 / 估值送負責人 → 主理合夥人。 只講這個流程在做什麼。
//
// 同一天移除四條 planned: capital_call / distribution / nav_valuation / fmv_valuation ——
// 它們早就在簽核引擎上線了 (V348 / V360, 說明在 flow-catalog.ts EVENT_DESCRIPTIONS),
// 卻還列在簽核路線頁的「規劃中 · 尚未實作」, capital_call 甚至同一頁出現兩次。
export const WORKFLOW_TYPE_CATALOG: ReadonlyArray<WorkflowTypeMeta> = [
  // === Cross — 跨領域 ===
  {
    key: 'access_request',
    scope: 'cross',
    label_zh: '申請加入',
    description: '一般成員申請加入基金或專案，核准後自動加上指派。',
    status: 'live',
    applies_to: ['fund', 'project'],
  },
  {
    key: 'resource_transfer',
    scope: 'cross',
    label_zh: '資源轉讓',
    description: '把某基金 / 專案的經理角色從 A 轉給 B（含繼任交接）。',
    status: 'planned',
  },

  // === System — 系統 ===
  {
    key: 'internal_signup_review',
    scope: 'system',
    label_zh: '申請審核',
    description: '新成員自行註冊並驗證 Email 後送審，核准即開通帳號（待審核 → 啟用），駁回則停權。',
    status: 'live',
  },
  {
    key: 'member_creation',
    scope: 'system',
    label_zh: '成員建立',
    description: '新成員加入公司（之後接 Entra OIDC）— 走核准，自動建立成員帳號。',
    status: 'planned',
  },
  {
    key: 'role_change',
    scope: 'system',
    label_zh: '角色變更',
    description: '提升 / 降級系統角色（一般成員 ↔ 管理員）。',
    status: 'planned',
  },
  {
    key: 'company_setting',
    scope: 'system',
    label_zh: '公司設定',
    description: '管理公司基本資料 / 預設參數調整（如預設管理費率）。',
    status: 'planned',
  },
  {
    key: 'dictionary_update',
    scope: 'system',
    label_zh: '字典更新',
    description: '新增 / 修改系統字典條目（產業 / 國家 / 投資者類型 ...）。',
    status: 'planned',
  },

  // === Fund — 基金 ===
  {
    key: 'fund_status_change',
    scope: 'fund',
    label_zh: '基金狀態變更',
    description:
      '基金任何狀態變更（含 籌備→募資 / 募資→投資 / 投資→收割 / 等等）— 由基金經理發起，主理合夥人核准後基金狀態自動更新。',
    status: 'live',
    applies_to: ['fund'],
  },
  {
    key: 'money_movement_review',
    scope: 'fund',
    label_zh: '金流覆核',
    description:
      '軟性 maker-checker — 發放 Call / 標記已收 / 一鍵入帳 後自動建立（即時生效不阻擋）。申請人綁基金經理、覆核者綁主理合夥人；事後覆核（✓ 已覆核）或退回（↩ 退回給申請人更正，需填理由）。SOC2 / 金管會 金流留痕控制。',
    status: 'live',
    applies_to: ['fund'],
  },
  {
    key: 'closing_open',
    scope: 'fund',
    label_zh: 'Closing 開設',
    description: '基金開新一輪 Closing — 核准後可接受 LP 承諾出資。',
    status: 'planned',
  },
  {
    key: 'gp_join',
    scope: 'fund',
    label_zh: 'GP 加入',
    description: '基金加入主理合夥人（GP 角色）。',
    status: 'planned',
  },
  {
    key: 'side_letter',
    scope: 'fund',
    label_zh: 'Side Letter 簽署',
    description: 'LP 跟基金簽 Side Letter（特殊條件）— 核准後存檔。',
    status: 'planned',
  },

  // === Project — 專案 ===
  {
    key: 'project_status_change',
    scope: 'project',
    label_zh: '專案狀態變更',
    description:
      '專案任何狀態變更（含 評估→持有 / 持有→部分退出 / 退出 / 沖銷 / 等等）— 由專案經理發起，主理合夥人核准後專案狀態自動更新。',
    status: 'live',
    applies_to: ['project'],
  },
  {
    key: 'commit_request',
    scope: 'project',
    label_zh: '投資承諾申請',
    description: '基金對專案提出出資承諾，由專案經理確認。',
    status: 'planned',
    applies_to: ['commitment'],
  },
  {
    key: 'round_create',
    scope: 'project',
    label_zh: '輪次建立',
    description: '專案開新一輪融資 (Series A/B/...) — 專案經理提交。',
    status: 'planned',
  },
  {
    key: 'project_exit',
    scope: 'project',
    label_zh: '專案退出',
    description: '專案退出 / 沖銷的專屬流程（含退出款項）— 目前先走「專案狀態變更」。',
    status: 'planned',
  },

  // === Investor — 投資人 ===
  {
    key: 'party_status_change',
    scope: 'investor',
    label_zh: '夥伴狀態變更',
    description:
      '夥伴（公司 / 個人）狀態變更（待審核 / 啟用 / 停用 / 暫停）— 由任一可見此夥伴的成員發起。待審核 → 啟用 強制檢查 KYC 狀態為已通過。',
    status: 'live',
    applies_to: ['party'],
  },
  {
    key: 'kyc_review',
    scope: 'investor',
    label_zh: 'KYC 審核',
    description: '新 LP KYC 文件審查 — 通過後投資者才能啟用。',
    status: 'planned',
    applies_to: ['investor', 'party'],
  },
  {
    key: 'investor_approve',
    scope: 'investor',
    label_zh: '投資者核准',
    description: '待審核的投資者升級為啟用 — 目前由管理員或投資主管直接變更狀態，不走簽核。',
    status: 'planned',
    applies_to: ['investor'],
  },
  {
    key: 'aml_review',
    scope: 'investor',
    label_zh: 'AML 風險評估',
    description: '高風險 LP 的 AML 評估。',
    status: 'planned',
  },
];

// UI helpers — type / scope / role label + emoji
export function workflowTypeLabel(type: string): string {
  const meta = WORKFLOW_TYPE_CATALOG.find((t) => t.key === type);
  return meta?.label_zh ?? type;
}

export function workflowScopeEmoji(scope: WorkflowScope): string {
  switch (scope) {
    case 'system':
      return '🌐';
    case 'fund':
      return '💼';
    case 'project':
      return '🚀';
    case 'investor':
      return '👥';
    case 'cross':
      return '🔗';
  }
}

// 2026-08-22 稽核: 這組名字是全站的**基準** (簽核當下顯示給簽核人看的名字),
// 但它原本是第五份 hardcode。 收斂到 lib/rbac-labels.ts —— 其餘四個呼叫端
// (TeamBadges / Fund·ProjectMembersModal / 離職精靈) 現在都讀同一份。
// ⚠ 這支對「不認得的角色」回空字串 (它被內嵌在句子裡), 跟 rbacRoleLabel() 的
//   「原樣回傳」不同, 所以不能直接換成 rbacRoleLabel。
export function workflowAssignedRoleLabel(role: WorkflowAssignedRole | null | undefined): string {
  if (!role) return '';
  return (RBAC_ROLE_LABEL as Record<string, string>)[role] ?? '';
}
